import { useMemo, useState } from "react";

import { convertRangeToNewYork, NEW_YORK } from "../../lib/newYorkLine";
import {
  formatNewYorkClock,
  readStatementSample,
  timeOfDayOf,
  type StatementSampleReading,
  type StatementSpan,
} from "../../lib/statementSample";
import { Button } from "../ui/button";
import type { components } from "../../api/schema";

type StatementSampleResponse = components["schemas"]["StatementSampleResponse"];
type StatementRange = components["schemas"]["StatementRangeResponse"];

// The zone picker (ADR 0039 D9.2). A thinkorswim statement prints wall clocks
// with no timezone in the file, the zone is hashed into every imported fill's
// external id, and it LOCKS on the first imported fill -- so this screen has one
// job: get the user to the right answer on the first try, with no nudge toward a
// wrong one.
//
// NOTHING IS PRE-SELECTED and submit starts DISABLED. A pre-selected zone is how
// a guess becomes a confirmed guess, and the old guess (America/Chicago) is
// exactly the defect ADR 0039 D9 exists to remove -- it filed every live fill
// two hours early. There is no fallback zone anywhere in this component.
//
// THE RAW TIMES DO NOT MOVE WITH THE SELECTION, and the copy never implies they
// do: a printed clock face is zone-free ground truth, which is the only reason it
// can be shown before a zone exists. The New York line is the one thing on the
// screen that responds to the choice.

const SELECT_CLASS =
  "flex h-9 w-full rounded-control border border-outline bg-surface-container px-3 py-1 text-sm text-on-surface shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50";

/** The 409: imported fills exist, so the zone the account uses is final. */
export type StatementTimezoneConflict = {
  readonly inForce: string;
  readonly requested: string;
};

/**
 * The IANA names offered, from the platform's own list.
 *
 * No new dependency and no hand-maintained list: `Intl.supportedValuesOf` is the
 * same tz database the conversion below reads, so the picker cannot offer a zone
 * the New York line could not convert.
 */
function platformTimezones(): readonly string[] {
  return Intl.supportedValuesOf("timeZone");
}

/**
 * The browser's zone, offered as a SUGGESTION and never selected.
 *
 * `null` rather than a fallback when it cannot be read. A fallback here would be
 * a default zone by another name, and this screen has none -- it is the whole
 * reason the endpoint takes a required field with no default.
 */
function browserTimezone(): string | null {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof resolved === "string" && resolved !== "" ? resolved : null;
}

/**
 * What a range line adds when the statement's own clock faces cross midnight.
 *
 * USES THE SERVER'S `wrapped`, and this is the one place that is correct. The
 * flag describes the STATEMENT's clock faces, which is exactly what these lines
 * print -- "fall between 23:50:00 and 00:10:00" is indistinguishable from a bug
 * without it, and the field exists on the wire for precisely this (see
 * StatementRangeResponse). The New York line is the opposite case and must
 * never read this: it prints New York's clock faces, and converting can both
 * add and remove a wrap.
 *
 * PER RANGE, never shared: one late fill can wrap the full range while the bulk
 * span that excludes it does not, so each line asks its own range.
 */
function wrapSuffix(range: StatementRange): string {
  return range.wrapped ? " the next morning" : "";
}

// --- the New York line ------------------------------------------------------
//
// The ONE place market language is permitted (ADR 0039 D9.2's amendment), and
// only once a zone is selected: before that there is no conditional to state.
// It converts the BULK span, each end from its OWN date, and works out its own
// midnight crossing from its own converted times -- never from the server's
// `wrapped`, which answers a question about the statement's clock faces rather
// than New York's and genuinely disagrees in both directions.
function NewYorkLine({ span, zone }: { span: StatementSpan; zone: string }) {
  const converted = convertRangeToNewYork({
    earliestWallClock: span.earliest.wall_clock,
    latestWallClock: span.latest.wall_clock,
    zone,
  });

  // A wall clock the chosen zone never read -- the spring-forward gap. Both
  // candidate instants would be invented, and the import itself refuses such a
  // time, so the picker must not promise a conversion it would refuse.
  if (!converted.show || converted.earliest.time === null || converted.latest.time === null) {
    return null;
  }

  const earliest = formatNewYorkClock(converted.earliest.time);
  const latest = formatNewYorkClock(converted.latest.time);
  // Keyed on the OUTPUT, not the zone name, so America/Toronto and every other
  // Eastern-offset zone takes the same branch. The advisory sentence is RETAINED
  // here: times already being New York times says nothing about whether New York
  // is the right answer.
  const times = earliest === latest ? earliest : `${earliest} to ${latest}`;

  return (
    <div
      data-testid="new-york-line"
      className="rounded-control border border-outline-variant bg-surface-container-high p-3 text-sm text-on-surface"
    >
      {converted.identicalToStatement ? (
        // THE BRANCH ABOVE IS KEYED ON THE OUTPUT. This inner check is not, and
        // the distinction is the whole point: WHETHER to show the special copy
        // is decided by the converted times coming out identical -- which is
        // what catches Toronto, Detroit, Nassau and every other Eastern-offset
        // zone without a list to maintain. WHICH CLAUSE to show is a sentence
        // choice, and the two zones are not interchangeable in it: New York IS
        // the market's own time; Toronto merely keeps the same clock as it.
        // Saying "America/Toronto, which is the market's own time" states
        // something false, which is what a browser read-through caught and no
        // test could. Swapping this check for an output test would not work --
        // both zones produce identical output, which is why they share a branch.
        zone === NEW_YORK ? (
          <p>
            You chose <strong>{zone}</strong>, which is the market&rsquo;s own time, so these
            times are already New York times: <strong>{times}</strong>.
          </p>
        ) : (
          <p>
            You chose <strong>{zone}</strong>. It runs on the same clock as New York, where the
            US market trades, so these times are already New York times:{" "}
            <strong>{times}</strong>.
          </p>
        )
      ) : (
        <p>
          You chose <strong>{zone}</strong>. In New York, where the US market trades, that makes{" "}
          {earliest === latest ? "this time" : "these times"} <strong>{times}</strong>
          {converted.wrapped === true ? ", carrying on past midnight" : ""}.
        </p>
      )}
      <p className="mt-1 italic text-on-surface-variant">
        If that doesn&rsquo;t look like your trading day, the timezone is probably wrong.
      </p>
    </div>
  );
}

// --- the four copy cases ----------------------------------------------------
//
// Driven by `basis` + `spread_seconds` (see readStatementSample), never by the
// client inspecting times. Raw strings are rendered VERBATIM; the range lines
// drop the date at the first space and the sample line keeps it, so the user can
// find that exact fill.
//
// The words "market hours", "regular hours", "pre-market", "after-hours" and
// "typical" appear nowhere here -- none of them is knowable without a zone. The
// single exception is the New York line above, which renders only after one is
// chosen.
function SampleCopy({ reading }: { reading: Exclude<StatementSampleReading, { kind: "unreadable" }> }) {
  if (reading.kind === "only-time") {
    return (
      <p data-testid="statement-sample-copy" className="text-sm text-on-surface">
        This statement has only one time in it: <strong>{reading.sample.raw}</strong>. Is that the
        time thinkorswim showed you? There&rsquo;s nothing else in the file to compare it against,
        so please check it carefully.
      </p>
    );
  }

  if (reading.kind === "narrow") {
    return (
      <p data-testid="statement-sample-copy" className="text-sm text-on-surface">
        All the times in this statement are within a few minutes of each other, around{" "}
        <strong>{reading.sample.raw}</strong>. There&rsquo;s little in the file to compare against,
        so please check it carefully.
      </p>
    );
  }

  return (
    <div data-testid="statement-sample-copy" className="space-y-2 text-sm text-on-surface">
      <p>
        Most of the times in this statement &mdash; <strong>19 out of every 20</strong> &mdash; fall
        between <strong>{timeOfDayOf(reading.bulk.earliest.raw)}</strong> and{" "}
        <strong>{timeOfDayOf(reading.bulk.latest.raw)}</strong>
        {wrapSuffix(reading.bulk)}.
      </p>
      {reading.extremes !== null && (
        <p data-testid="statement-extremes-line" className="text-on-surface-variant">
          The full range, including the earliest and latest, is{" "}
          <strong>{timeOfDayOf(reading.extremes.earliest.raw)}</strong> to{" "}
          <strong>{timeOfDayOf(reading.extremes.latest.raw)}</strong>
          {wrapSuffix(reading.extremes)}.
        </p>
      )}
      <p>
        One of them, from the middle of that range, reads <strong>{reading.sample.raw}</strong>. Is
        that the time thinkorswim showed you?
      </p>
    </div>
  );
}

export type StatementTimezonePickerProps = {
  /** The body of `POST /api/imports/statement-sample`. */
  sample: StatementSampleResponse;
  onSubmit: (timezone: string) => void;
  /** The only control offered when the file yielded no times. */
  onChooseDifferentFile: () => void;
  submitting?: boolean;
  /**
   * Set once the account's zone turned out to be locked (the 409). The zone is
   * read off the RESPONSE BODY, not a re-fetch -- see
   * StatementTimezoneAlreadySetError.
   */
  conflict?: StatementTimezoneConflict | null;
  /** A field-level failure (a 422, a 404, a transport error). */
  error?: string | null;
  /**
   * The zone already in force, when this is a CHANGE rather than a first choice.
   *
   * Named on screen, because a user changing a zone has to see what they are
   * changing from -- and because the change is only possible until the first
   * import, which the copy already states. Still nothing pre-selected: the
   * current zone is shown, not loaded into the control, so a change is always an
   * explicit new answer to the same question.
   */
  currentTimezone?: string | null;
  /** Offered only on a change, where there is a previous state to return to. */
  onCancel?: () => void;
  /**
   * The zones offered. Defaults to the platform's list; injected by tests so
   * nothing reads the host environment for its expectations.
   */
  timezones?: readonly string[];
  /**
   * The browser's own zone, shown as a labelled guess. `undefined` reads it from
   * the platform; `null` suppresses the suggestion. Injected by tests for the
   * same reason as `timezones`.
   */
  suggestedTimezone?: string | null;
};

export function StatementTimezonePicker({
  sample,
  onSubmit,
  onChooseDifferentFile,
  submitting = false,
  conflict = null,
  error = null,
  currentTimezone = null,
  onCancel,
  timezones,
  suggestedTimezone,
}: StatementTimezonePickerProps) {
  const [selected, setSelected] = useState("");
  const reading = useMemo(() => readStatementSample(sample), [sample]);
  const zones = useMemo(() => timezones ?? platformTimezones(), [timezones]);
  const suggestion = suggestedTimezone === undefined ? browserTimezone() : suggestedTimezone;

  // THE LOCK. Imported fills exist, so the zone the account reads statements in
  // is final -- and the one in force is named, because "that didn't work" leaves
  // the user unable to tell which zone their trades were filed under. No select
  // and no submit: an edit affordance that cannot succeed is worse than none.
  if (conflict !== null) {
    return (
      <div
        role="alert"
        data-testid="statement-timezone-conflict"
        className="space-y-2 rounded-control border border-warning bg-warning/10 p-4 text-sm text-on-surface"
      >
        <strong>This account&rsquo;s timezone is already set</strong>
        <p className="text-on-surface-variant">
          Its statements are read as <strong>{conflict.inForce}</strong>, and trades have already
          been imported under that reading, so it can&rsquo;t be changed now &mdash; the timezone is
          part of how every imported trade is identified. Nothing was changed.
        </p>
      </div>
    );
  }

  // NO ZONE CHOICE AT ALL when the file yielded no times, and the reason is in
  // the code rather than caution: /validate resolves the zone BEFORE it parses,
  // so on a zone-less account it refuses without ever looking at the file. A
  // user whose file is wrong would otherwise have to set a zone -- the one
  // decision that locks -- purely to find that out. A file with no readable
  // execution times cannot import under any zone, so offering one costs nothing
  // and risks the lock.
  if (reading.kind === "unreadable") {
    return (
      <div
        role="alert"
        data-testid="statement-sample-unreadable"
        className="space-y-3 rounded-control border border-outline bg-surface-container p-4 text-sm text-on-surface"
      >
        <p>
          We couldn&rsquo;t read any execution times from this file. Check that you uploaded a
          thinkorswim <strong>Account Statement</strong> (Monitor &rarr; Account Statement), then try
          again.
        </p>
        <Button type="button" variant="outline" onClick={onChooseDifferentFile}>
          Choose a different file
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="statement-timezone-picker"
      role="region"
      aria-label="Set statement timezone"
      className="space-y-4 rounded-control border border-outline bg-surface-container p-4 shadow-sm"
    >
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-on-surface">
          What timezone does your thinkorswim display times in?
        </h3>
        {currentTimezone !== null && (
          <p data-testid="statement-timezone-current" className="text-sm text-on-surface-variant">
            This account currently reads statements as <strong>{currentTimezone}</strong>.
          </p>
        )}
        <SampleCopy reading={reading} />
      </div>

      <label className="flex max-w-sm flex-col gap-1 text-sm text-on-surface">
        Statement timezone
        <select
          className={SELECT_CLASS}
          value={selected}
          disabled={submitting}
          onChange={(event) => setSelected(event.target.value)}
        >
          <option value="" disabled>
            Choose a timezone&hellip;
          </option>
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </select>
      </label>

      {suggestion !== null && (
        <p data-testid="statement-timezone-suggestion" className="text-xs text-on-surface-variant">
          Your computer is set to <strong>{suggestion}</strong>. That&rsquo;s a guess about where you
          are, not about what thinkorswim displays &mdash; check thinkorswim before you choose.
        </p>
      )}

      {selected !== "" && <NewYorkLine span={reading.newYork} zone={selected} />}

      {error !== null && (
        <p role="alert" data-testid="statement-timezone-error" className="text-sm text-danger">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={() => onSubmit(selected)} disabled={selected === "" || submitting}>
          {submitting ? "Saving…" : "Set timezone"}
        </Button>
        {onCancel !== undefined && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <span className="text-xs text-on-surface-variant">
          You can change this until you import your first statement.
        </span>
      </div>
    </div>
  );
}
